import { Controller } from "@hotwired/stimulus"
import { celebrate, resetCelebrations, resetSparkles, sparkleAt } from "../utils/fun_celebrate"
import { FUN_SETTINGS_EVENT, applyFunSettings, applyFunSkin } from "../utils/fun_settings"

// Le son, la musique, les danseurs du disco et les scènes des autres skins : un module à part
// (utils/fun_extras.js), chargé au montage de ce contrôleur — donc pour les seuls comptes en mode
// fun.
const loadExtras = () => import("../utils/fun_extras")

// Le chef d'orchestre du mode fun, monté sur le `<body>` (cf. layouts/_body.html.erb) et donc
// remonté à chaque visite Turbo.
//
// Il fait six choses, et surtout il en fait UNE que personne d'autre ne peut faire : écouter
// les succès de toute l'application sans qu'un seul composant métier ait à s'en soucier.
//
//   1. il applique les réglages du navigateur (icônes / confettis / micro-interactions /
//      bruitages / décor, skin) et les protège des rafraîchissements en morph ;
//   2. il fête les messages de succès — le conteneur des flashs est le point de passage
//      obligé de tout ce qui réussit dans l'application, du contrat souscrit à l'export lancé,
//      donc un seul branchement suffit là où il en aurait fallu une dizaine ;
//   3. il fait étinceler les boutons au clic, et sonner les clics dans la voix du skin, par la
//      même délégation ;
//   4. il tient la musique du skin à jour — elle, en revanche, survit aux visites (cf.
//      utils/fun_music.js) : ce contrôleur se démonte à chaque page, pas elle — et habille les
//      danseurs du disco (cf. utils/disco_dancers.js, utils/disco_idle.js) et l'équipe des autres
//      skins (cf. utils/fun_scenes.js) ;
//   5. il tient les deux surprises : le konami code et le tonneau du logo ;
//   6. il range derrière lui (ni le combo ni les étincelles ne survivent à une sortie).
export default class extends Controller {
  static KONAMI = [
    "ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown",
    "ArrowLeft", "ArrowRight", "ArrowLeft", "ArrowRight", "b", "a"
  ]

  static RAINBOW_MS = 2400
  static BARREL_ROLL_MS = 900

  // Ce qui fait un bruit au clic : ce qui agit — boutons, liens, onglets, éléments de menu, cases.
  static SOUNDING = [
    "a[href]", "button", "summary", ".btn", ".dropdown-item",
    '[role="button"]', '[role="tab"]', '[role="menuitem"]',
    'input[type="checkbox"]', 'input[type="radio"]'
  ].join(", ")

  connect() {
    applyFunSettings()
    applyFunSkin()
    this.konamiProgress = 0
    this.extras = loadExtras()

    this.onSettings = () => {
      applyFunSettings()
      this.extras.then((extras) => {
        extras.syncFunMusic()
        extras.syncDiscoDancers()
        extras.syncFunScenes()
      })
    }
    this.onBeforeMorphAttribute = (event) => this.keepBodyState(event)
    this.onKeydown = (event) => {
      this.wakeMusic()
      this.konami(event)
    }
    this.onDoubleClick = (event) => this.barrelRoll(event)
    this.onClick = (event) => {
      this.sparkle(event)
      this.clickSound(event)
    }
    this.onPointerdown = () => this.wakeMusic()

    window.addEventListener(FUN_SETTINGS_EVENT, this.onSettings)
    document.addEventListener("turbo:before-morph-attribute", this.onBeforeMorphAttribute)
    // Sur `document` et pas en `data-action` sur le body : le body est partagé par tout le
    // monde, et le mode fun n'a aucune raison d'ajouter une ligne dans son attribut.
    document.addEventListener("keydown", this.onKeydown)
    document.addEventListener("dblclick", this.onDoubleClick)
    // En capture : un bouton qui arrête la propagation (il y en a, dans les menus) doit quand
    // même étinceler. La phase de capture passe avant tout `stopPropagation` du chemin.
    document.addEventListener("click", this.onClick, true)
    document.addEventListener("pointerdown", this.onPointerdown, true)

    this.watchFlashes()
    this.celebrateExistingFlash()
    this.extras
      .then((extras) => {
        extras.syncFunMusic()
        extras.syncDiscoDancers()
        extras.watchDiscoIdle()
        extras.syncFunScenes()
        extras.watchFunIdle()
      })
      .catch((error) => console.warn("[fun] son et danseurs indisponibles", error))
  }

  disconnect() {
    window.removeEventListener(FUN_SETTINGS_EVENT, this.onSettings)
    document.removeEventListener("turbo:before-morph-attribute", this.onBeforeMorphAttribute)
    document.removeEventListener("keydown", this.onKeydown)
    document.removeEventListener("dblclick", this.onDoubleClick)
    document.removeEventListener("click", this.onClick, true)
    document.removeEventListener("pointerdown", this.onPointerdown, true)
    this.flashObserver?.disconnect()
    clearTimeout(this.rainbowTimer)
    resetCelebrations()
    resetSparkles()
  }

  // --- l'état du body -----------------------------------------------------------------------

  // Un rafraîchissement en morph aligne les attributs du body sur ceux du serveur, qui ne sait
  // rien du navigateur : il rallumerait les interrupteurs coupés et arracherait le skin (et le
  // thème qu'il impose) jusqu'à la visite suivante. Ces attributs-là appartiennent au navigateur,
  // on les garde.
  keepBodyState(event) {
    if (event.target !== document.body) return

    const { attributeName } = event.detail
    const skinned = Boolean(document.body.dataset.funSkin)
    if (attributeName.startsWith("data-fun-") || (skinned && attributeName === "data-bs-theme")) {
      event.preventDefault()
    }
  }

  // --- les succès ---------------------------------------------------------------------------

  // Un flash rendu par le serveur est déjà là quand le contrôleur se monte ; un flash arrivé
  // par turbo-stream arrive après. Les deux comptent, d'où l'observateur ET la passe initiale.
  celebrateExistingFlash() {
    if (this.flashContainer?.querySelector(".alert-info, .alert-success")) celebrate("spark")
  }

  watchFlashes() {
    const container = this.flashContainer
    if (!container) return

    this.flashObserver = new MutationObserver((mutations) => {
      const celebrated = mutations.some((mutation) => (
        [...mutation.addedNodes].some((node) => (
          node.nodeType === Node.ELEMENT_NODE &&
            (node.matches?.(".alert-info, .alert-success") ||
              node.querySelector?.(".alert-info, .alert-success"))
        ))
      ))

      if (celebrated) celebrate("spark")
    })

    this.flashObserver.observe(container, { childList: true, subtree: true })
  }

  get flashContainer() {
    return document.getElementById("flashes-container")
  }

  // --- l'étincelle du clic ------------------------------------------------------------------

  // Sur `.btn` seulement, et pas sur tout ce qui est cliquable : un lien de navigation, une
  // ligne de tableau ou une case à cocher se cliquent en rafale, et une interface qui pétille
  // au moindre mouvement cesse d'être une interface.
  //
  // Le dosage vit dans `sparkleAt` (réglages, mouvement réduit, étranglement) ; ici on ne fait
  // que décider QUOI mérite une étincelle et OÙ elle part.
  sparkle(event) {
    const button = event.target.closest?.(".btn")
    if (!button || button.disabled || button.classList.contains("disabled")) return
    if (button.getAttribute("aria-disabled") === "true") return

    // Un clic déclenché au clavier (Entrée sur un bouton focalisé) arrive sans coordonnées :
    // on part alors du centre du bouton plutôt que du coin haut-gauche de la page.
    const rect = button.getBoundingClientRect()
    const x = event.clientX || rect.left + rect.width / 2
    const y = event.clientY || rect.top + rect.height / 2

    sparkleAt(x, y)
  }

  // --- le son -------------------------------------------------------------------------------

  // Un bruit par clic, dans la voix du skin porté (cf. utils/fun_click_sounds.js). Contrairement
  // aux étincelles, tout ce qui agit sonne, liens et cases compris : un bruit ne cache rien de
  // l'écran, et c'est l'interrupteur « Bruitages » qui dose. Rien sans skin : le costume fait la
  // voix.
  clickSound(event) {
    const body = document.body
    const skin = body.dataset.funSkin
    if (!skin || body.dataset.funSounds !== "on") return

    const target = event.target.closest?.(this.constructor.SOUNDING)
    if (!target || target.disabled || target.getAttribute("aria-disabled") === "true") return

    const toggle = target.matches('input[type="checkbox"], input[type="radio"]')
    const variant = toggle ? (target.checked ? "on" : "off") : "press"
    // Module déjà chargé au montage : la promesse est tenue, le son part dans la foulée du clic.
    this.extras.then((extras) => extras.playClickSound(skin, variant))
  }

  // La musique ne peut démarrer que dans un geste de l'utilisateur (politique d'autolecture des
  // navigateurs) : chaque geste lui en donne l'occasion, tant qu'elle ne joue pas.
  wakeMusic() {
    this.extras.then((extras) => {
      if (!extras.isFunMusicPlaying()) extras.syncFunMusic()
    })
  }

  // --- les surprises ------------------------------------------------------------------------

  // La séquence est assez improbable pour ne jamais se déclencher par accident, mais on sort
  // quand même dès qu'on est dans un champ de saisie : le mode fun n'a aucune raison
  // d'observer ce que les gens tapent dans un formulaire.
  konami(event) {
    if (this.typingInAField(event.target)) return

    const expected = this.constructor.KONAMI[this.konamiProgress]
    if (event.key !== expected && event.key?.toLowerCase() !== expected) {
      this.konamiProgress = event.key === this.constructor.KONAMI[0] ? 1 : 0
      return
    }

    this.konamiProgress += 1
    if (this.konamiProgress < this.constructor.KONAMI.length) return

    this.konamiProgress = 0
    celebrate("party")
    this.rainbow()
  }

  typingInAField(target) {
    if (!(target instanceof HTMLElement)) return false
    return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)
  }

  rainbow() {
    document.body.classList.add("fun-mode--rainbow")
    clearTimeout(this.rainbowTimer)
    this.rainbowTimer = setTimeout(
      () => document.body.classList.remove("fun-mode--rainbow"),
      this.constructor.RAINBOW_MS
    )
  }

  // Double-clic sur le logo de la plateforme. En délégation : le logo est rendu par trois
  // composants différents et se recharge dans des turbo-frames.
  barrelRoll(event) {
    const logo = event.target.closest?.(".platform-logo, .brand-logo, .brand-logo-solid")
    if (!logo) return

    logo.classList.remove("fun-barrel-roll")
    void logo.offsetWidth // force le redémarrage de l'animation si on double-clique deux fois
    logo.classList.add("fun-barrel-roll")
    setTimeout(
      () => logo.classList.remove("fun-barrel-roll"),
      this.constructor.BARREL_ROLL_MS
    )
  }
}
